import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { PasswordStrength, allCriteriaMet } from '@/components/PasswordStrength';
import { sanitizeUsername, isValidUsername, getUsernameError } from '@/lib/usernameValidation';

const monoFont = "'IBM Plex Mono', monospace";

interface InviteData {
  id: string;
  email: string | null;
  username: string | null;
  site_name: string | null;
  plan: string;
  status: string;
  expires_at: string;
}

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) { setInvalid(true); setLoading(false); return; }

      // Track open before any validation
      await supabase.rpc('track_invite_open', { p_token: token } as any);

      const { data } = await supabase.rpc('get_invite_by_token', { p_token: token });
      if (!data || (data as any[]).length === 0) {
        setInvalid(true);
        setLoading(false);
        return;
      }
      const inv = (data as any[])[0];
      if (!['pending', 'opened'].includes(inv.status) || new Date(inv.expires_at).getTime() < Date.now()) {
        setInvalid(true);
        setLoading(false);
        return;
      }
      setInvite(inv);
      if (inv.email) setEmail(inv.email);
      setLoading(false);
    };
    loadInvite();
  }, [token]);

  // Debounced username check
  useEffect(() => {
    if (!username || !isValidUsername(username)) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    const timeout = setTimeout(async () => {
      const { data } = await supabase.rpc('check_username_available', { p_username: username });
      setUsernameAvailable(!!data);
      setCheckingUsername(false);
    }, 400);
    return () => clearTimeout(timeout);
  }, [username]);

  const handleUsernameChange = useCallback((val: string) => {
    setUsername(sanitizeUsername(val).slice(0, 20));
  }, []);

  const passwordValid = allCriteriaMet(password);
  const canSubmit = passwordValid && password === confirmPassword && name.trim().length > 0
    && email.trim().length > 0 && isValidUsername(username) && usernameAvailable === true && !creating;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) {
      setError('A senha não atende todos os requisitos.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    if (usernameAvailable !== true) {
      setError('Escolha um username disponível.');
      return;
    }

    setCreating(true);

    // Update invite with user-provided data
    await supabase.rpc('update_invite_user_data', {
      p_token: token!,
      p_username: username,
      p_site_name: name,
      p_email: email,
    } as any);

    let userId: string | null = null;

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: name },
        emailRedirectTo: `https://${username}.criaaa.com/admin`,
      },
    });

    const isRepeatedSignup = !signUpError && signUpData.user && 
      (!signUpData.user.identities || signUpData.user.identities.length === 0);

    if (signUpError || isRepeatedSignup) {
      // Either explicit error or repeated signup (fake user returned) — try sign in
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) {
        if (!signUpError) {
          setError('Este e-mail já possui conta. Verifique a senha informada.');
        } else {
          setError(signUpError?.message || 'Erro ao criar conta.');
        }
        setCreating(false);
        return;
      }
      userId = signInData.user?.id || null;

      // Existing user — accept invite and redirect directly
      if (userId) {
        const { data: accepted } = await supabase.rpc('accept_invite', {
          p_token: token!,
          p_user_id: userId,
        });
        if (!accepted) {
          setError('Erro ao processar convite. Tente novamente.');
          setCreating(false);
          return;
        }
      }
      window.location.href = `https://${username}.criaaa.com/admin`;
      return;
    }

    // Fresh signup succeeded
    userId = signUpData.user?.id || null;

    if (userId) {
      const { data: accepted } = await supabase.rpc('accept_invite', {
        p_token: token!,
        p_user_id: userId,
      });
      if (!accepted) {
        setError('Erro ao processar convite. Tente novamente.');
        setCreating(false);
        return;
      }
    }

    // Redirect to verify-email page (email confirmation required)
    window.location.href = `/verificar-email?email=${encodeURIComponent(email)}`;
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 14, fontWeight: 300, width: '100%',
    padding: '10px 12px', border: '1px solid rgba(0,0,0,0.15)',
    backgroundColor: 'rgba(0,0,0,0.03)', color: '#000000', borderRadius: 0,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 11, fontWeight: 300, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)', marginBottom: 6, display: 'block',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F6F2', fontFamily: monoFont, color: 'rgba(0,0,0,0.4)' }}>
        Carregando...
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F6F2' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontFamily: monoFont, fontSize: 24, fontWeight: 300, color: '#000000', marginBottom: 16 }}>CRIAAA</div>
          <div style={{ fontFamily: monoFont, fontSize: 14, fontWeight: 300, color: 'rgba(0,0,0,0.6)', lineHeight: 1.6 }}>
            Este convite expirou, já foi utilizado ou é inválido.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F6F2' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 24 }}>
        <div style={{ fontFamily: monoFont, fontSize: 24, fontWeight: 300, color: '#000000', marginBottom: 32, textAlign: 'center' }}>
          CRIAAA
        </div>

        <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(0,0,0,0.6)', marginBottom: 4, textAlign: 'center' }}>
          Você foi convidado.
        </div>
        <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(0,0,0,0.6)', marginBottom: 24, textAlign: 'center' }}>
          Crie sua conta para começar.
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Nome</label>
            <input value={name} onChange={e => setName(e.target.value)} required style={inputStyle} placeholder="Seu nome completo" />
          </div>
          <div>
            <label style={labelStyle}>E-mail</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} placeholder="seu@email.com" />
          </div>
          <div>
            <label style={labelStyle}>Nome do site</label>
            <input value={username} onChange={e => handleUsernameChange(e.target.value)} required style={inputStyle} placeholder="ex: joao" />
            <div style={{ fontFamily: monoFont, fontSize: 11, fontWeight: 300, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'rgba(0,0,0,0.35)' }}>
                {username ? `${username}.criaaa.com` : '...'}
              </span>
              {username.length > 0 && getUsernameError(username) ? (
                <span style={{ color: '#ff4444' }}>{getUsernameError(username)}</span>
              ) : isValidUsername(username) ? (
                <span style={{ color: checkingUsername ? 'rgba(0,0,0,0.3)' : usernameAvailable ? '#4ade80' : '#ff4444' }}>
                  {checkingUsername ? 'verificando...' : usernameAvailable ? 'Disponível ✓' : 'Este endereço já está em uso'}
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Senha</label>
            <PasswordStrength password={password} theme="light" />
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} placeholder="Crie uma senha segura" />
          </div>
          <div>
            <label style={labelStyle}>Confirmar senha</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required style={inputStyle} />
          </div>

          {error && <div style={{ fontFamily: monoFont, fontSize: 11, color: '#ff4444' }}>{error}</div>}

          <button type="submit" disabled={!canSubmit}
            style={{
              fontFamily: monoFont, fontSize: 13, fontWeight: 300, textTransform: 'uppercase',
              padding: '12px 16px', border: '1px solid rgba(0,0,0,0.2)', backgroundColor: '#000',
              color: '#F7F6F2', cursor: canSubmit ? 'pointer' : 'default', marginTop: 8,
              transition: 'opacity 0.15s ease',
              opacity: canSubmit ? 1 : 0.3,
              pointerEvents: canSubmit ? 'auto' : 'none',
              borderRadius: 0,
            }}>
            {creating ? 'CRIANDO...' : 'CRIAR CONTA'}
          </button>
        </form>
      </div>
    </div>
  );
}
